// __PRESENTACION_DEMANDA_V71__
// LexDuo · Pestaña "Presentación de demanda" del módulo Ejecuciones.
// Visible cuando la ejecución alcanza estado 'apta_demanda' o posterior.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { Send, Upload, FileText, CheckCircle2, AlertCircle, Trash2, Eye } from 'lucide-react';
import { obtenerToken } from '../../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = (extra = {}) => {
  const tok = obtenerToken();
  return { ...(tok ? { Authorization: `Bearer ${tok}` } : {}), ...extra };
};

const fmtEUR = (n) => Number(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const PresentacionDemanda = ({ ejecucionId, ejecucion, onChanged }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [destinatario, setDestinatario] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [adjuntoIds, setAdjuntoIds] = useState([]);

  const cargar = useCallback(async () => {
    if (!ejecucionId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/demanda/preview`, { headers: headers() });
      setPreview(r.data);
      setDestinatario(r.data?.email?.to || '');
      setAsunto(r.data?.email?.asunto || '');
      setCuerpo(r.data?.email?.cuerpo || '');
      const elegibles = (r.data?.adjuntos || []).filter(a => ['demanda','demanda_ejecucion','documental_demanda'].includes(a.tipo));
      setAdjuntoIds(elegibles.map(a => a.id));
    } catch (e) {
      toast({ title: 'No se pudo cargar el preview', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [ejecucionId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (e, tipo) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('tipo', tipo);
        fd.append('file', f);
        await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos`, fd,
          { headers: headers({ 'Content-Type': 'multipart/form-data' }) });
      }
      toast({ title: `${files.length} archivo(s) subido(s)` });
      await cargar();
    } catch (err) {
      toast({ title: 'Error subiendo', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setUploading(false); e.target.value = ''; }
  };

  const borrarAdj = async (id) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos/${id}`, { headers: headers() });
      await cargar();
    } catch (err) {
      toast({ title: 'Error eliminando', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const presentar = async () => {
    if (!destinatario) { toast({ title: 'Falta destinatario', variant: 'destructive' }); return; }
    if (!adjuntoIds.length) { toast({ title: 'Selecciona al menos un adjunto', variant: 'destructive' }); return; }
    if (!window.confirm(`¿Confirmas el envío al procurador ${destinatario}?\n\nEsto:\n· Enviará el email con los ${adjuntoIds.length} adjunto(s)\n· Cambiará el estado de la ejecución a "demanda presentada"\n· Creará una actuación en el expediente principal con los adjuntos`)) return;
    setEnviando(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/demanda/presentar`,
        { destinatario_email: destinatario, asunto, cuerpo, adjunto_ids: adjuntoIds },
        { headers: headers(), timeout: 120000 });
      toast({ title: 'Demanda presentada', description: `Email enviado y actuación #${r.data.actuacion_id} creada.` });
      await cargar();
      onChanged?.();
    } catch (err) {
      toast({ title: 'Error en envío', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  const yaPresentada = preview?.ejecucion?.estado && preview.ejecucion.estado !== 'apta_demanda';

  if (loading) return <div className="p-6 text-gray-500" data-testid="presentacion-loading">Cargando…</div>;
  if (!preview) return null;

  return (
    <div className="space-y-4" data-testid="tab-presentacion-demanda">
      {yaPresentada && preview.ejecucion.fecha_presentacion_demanda && (
        <Card className="p-4 bg-emerald-50 border-emerald-300 flex items-center gap-3" data-testid="banner-demanda-presentada">
          <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-900">Demanda presentada</div>
            <div className="text-xs text-emerald-800">Fecha: {new Date(preview.ejecucion.fecha_presentacion_demanda).toLocaleString('es-ES')}</div>
          </div>
        </Card>
      )}

      {/* Resumen del destinatario */}
      <Card className="p-4">
        <div className="text-xs font-semibold uppercase text-violet-700 mb-2">Datos del envío</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs">Cuenta remitente</Label>
            <div className="font-mono text-xs bg-gray-50 border rounded px-2 py-1.5 mt-1">{preview.email.from_pref}</div>
          </div>
          <div>
            <Label className="text-xs">Procurador del expediente</Label>
            <div className="text-sm bg-gray-50 border rounded px-2 py-1.5 mt-1 truncate" title={(preview.expediente.procurador?.nombre || '')+' '+(preview.expediente.procurador?.apellidos || '')}>
              {preview.expediente.procurador?.nombre || ''} {preview.expediente.procurador?.apellidos || ''}
              {!preview.expediente.procurador?.id && <span className="text-red-600 italic">— sin procurador asignado</span>}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Email destinatario</Label>
          <Input value={destinatario} onChange={e => setDestinatario(e.target.value)}
                 placeholder="procurador@…"
                 disabled={yaPresentada}
                 data-testid="input-destinatario-demanda" />
        </div>
      </Card>

      {/* Adjuntos */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase text-violet-700">Documentos a enviar ({preview.adjuntos.filter(a => adjuntoIds.includes(a.id)).length})</div>
          {!yaPresentada && (
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input type="file" hidden multiple accept="application/pdf"
                       onChange={(e) => subir(e, 'demanda_ejecucion')}
                       data-testid="input-upload-demanda" />
                <span className={`inline-flex items-center gap-1 text-xs border border-violet-300 text-violet-700 rounded px-3 py-1.5 hover:bg-violet-50 ${uploading ? 'opacity-50' : ''}`}>
                  <Upload className="w-3.5 h-3.5" /> Subir demanda
                </span>
              </label>
              <label className="cursor-pointer">
                <input type="file" hidden multiple
                       onChange={(e) => subir(e, 'documental_demanda')}
                       data-testid="input-upload-documentales" />
                <span className={`inline-flex items-center gap-1 text-xs border border-gray-300 text-gray-700 rounded px-3 py-1.5 hover:bg-gray-50 ${uploading ? 'opacity-50' : ''}`}>
                  <Upload className="w-3.5 h-3.5" /> Subir documentales
                </span>
              </label>
            </div>
          )}
        </div>
        {preview.adjuntos.length === 0 ? (
          <div className="text-sm text-gray-500 italic py-4 text-center border border-dashed rounded">No hay documentos. Sube la demanda y sus documentales.</div>
        ) : (
          <div className="space-y-1.5">
            {preview.adjuntos.map(a => (
              <div key={a.id} className={`flex items-center gap-2 text-xs border rounded px-2 py-1.5 ${adjuntoIds.includes(a.id) ? 'bg-violet-50 border-violet-200' : 'bg-gray-50'}`} data-testid={`adj-row-${a.id}`}>
                <input type="checkbox" checked={adjuntoIds.includes(a.id)} disabled={yaPresentada}
                       onChange={(e) => setAdjuntoIds(ids => e.target.checked ? [...ids, a.id] : ids.filter(x => x !== a.id))} />
                <FileText className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <span className="flex-1 truncate font-medium" title={a.nombre_archivo}>{a.nombre_archivo}</span>
                <Badge variant="outline" className="text-[10px]">{a.tipo}</Badge>
                <span className="text-gray-400 tabular-nums">{Math.round((a.tamano_bytes || 0)/1024)} KB</span>
                {!yaPresentada && (
                  <button onClick={() => borrarAdj(a.id)} className="text-red-500 hover:text-red-700" data-testid={`btn-borrar-adj-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Importes desglosados (informativo) */}
      <Card className="p-4 bg-blue-50/50 border-blue-200">
        <div className="text-xs font-semibold uppercase text-blue-700 mb-2">Importes solicitados (se incluirán en la actuación)</div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div><div className="text-gray-500">Principal</div><div className="font-mono font-semibold">{fmtEUR(preview.importes.principal)}</div></div>
          <div><div className="text-gray-500">Intereses</div><div className="font-mono font-semibold">{fmtEUR(preview.importes.intereses_vencidos)}</div></div>
          <div><div className="text-gray-500">Costas</div><div className="font-mono font-semibold">{fmtEUR(preview.importes.costas)}</div></div>
          <div><div className="text-gray-500">TOTAL</div><div className="font-mono font-bold text-blue-900">{fmtEUR(preview.importes.total)}</div></div>
        </div>
      </Card>

      {/* Email */}
      <Card className="p-4">
        <div className="text-xs font-semibold uppercase text-violet-700 mb-2">Email al procurador</div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Asunto</Label>
            <Input value={asunto} onChange={e => setAsunto(e.target.value)} disabled={yaPresentada} data-testid="input-asunto-demanda" />
          </div>
          <div>
            <Label className="text-xs">Cuerpo</Label>
            <Textarea value={cuerpo} onChange={e => setCuerpo(e.target.value)} rows={9} disabled={yaPresentada} data-testid="textarea-cuerpo-demanda" className="font-mono text-xs" />
          </div>
        </div>
      </Card>

      {/* Acción */}
      {!yaPresentada && (
        <div className="flex justify-end items-center gap-3 sticky bottom-0 bg-white border-t pt-3">
          {!destinatario && (
            <span className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Falta email destinatario</span>
          )}
          <Button onClick={presentar} disabled={enviando || !destinatario || !adjuntoIds.length}
                  className="bg-violet-700 hover:bg-violet-800 text-white"
                  data-testid="btn-presentar-demanda">
            <Send className="w-4 h-4 mr-1.5" />
            {enviando ? 'Enviando…' : 'Presentar demanda y enviar al procurador'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PresentacionDemanda;
